/**
 * @file CountryPicker.tsx
 * @brief Country selection control — pill trigger + searchable modal sheet.
 *
 * @details
 * Mirrors mobile `VZCountryPicker` / company-admin `LanguagePicker` UX:
 * count in title, flagcdn images, contextual subtitle support.
 *
 * @author DES
 * @date 26-05-2026
 */

import { useMemo, useState } from "react";
import { ChevronsUpDown, Earth } from "lucide-react";
import { CountryFlag } from "@/components/ui/CountryFlag";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { COUNTRIES, findCountry, type Country } from "@/shared/data/countries";
import { cn } from "@/shared/theme/cn";

interface CountryPickerProps {
  /** The currently held value — the country *name* (e.g. "United Kingdom"). */
  value?: string | null;
  /** Called with the new country *name* (or `null` when cleared). */
  onChange: (countryName: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  /** Override modal title (default includes catalogue count). */
  sheetTitle?: string;
  /** Override modal subtitle for context (company, billing, etc.). */
  sheetSubtitle?: string;
}

interface CountryPickerItem extends PickerSheetItem {
  country: Country;
}

function buildItems(): CountryPickerItem[] {
  return COUNTRIES.map((country) => ({
    id: country.name,
    label: country.name,
    subtitle: country.code,
    searchKeys: [country.code],
    country,
  }));
}

export function CountryPicker({
  value,
  onChange,
  placeholder = "Select a country…",
  disabled,
  size = "md",
  sheetTitle,
  sheetSubtitle = "Search by country name or ISO code.",
}: CountryPickerProps) {
  const [open, setOpen] = useState(false);

  const matched = useMemo(() => findCountry(value), [value]);
  const triggerLabel = matched?.name ?? value ?? null;

  const items = useMemo(() => buildItems(), []);

  const modalTitle =
    sheetTitle ?? (items.length > 0 ? `Select country (${items.length})` : "Select country");

  const triggerHeight = size === "sm" ? "h-9" : "h-10";

  return (
    <>
      <PickerSheet<CountryPickerItem>
        anchor={
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "flex w-full items-center justify-between rounded-full border border-input bg-field px-5 text-sm transition-colors",
              triggerHeight,
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50",
              !triggerLabel && "text-muted-foreground",
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              {matched ? (
                <CountryFlag code={matched.code} size="sm" />
              ) : (
                <Earth className="h-4 w-4 shrink-0 opacity-60" />
              )}
              <span className="truncate">{triggerLabel ?? placeholder}</span>
            </span>
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </button>
        }
        open={open}
        onOpenChange={setOpen}
        title={modalTitle}
        subtitle={sheetSubtitle}
        searchPlaceholder="Search countries…"
        items={items}
        selectedId={matched?.name ?? null}
        onSelect={(item) => onChange(item.country.name)}
        onClear={() => onChange(null)}
        renderLeftAccessory={(item) => <CountryFlag code={item.country.code} size="lg" />}
      />
    </>
  );
}
